import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { exchangeCodeForToken, isAuthenticated } from '@/integrations/keycloak/client';
import { consumePostLoginRedirect } from '@/utils/postLoginRedirect';
import {
  clearLastRealm,
  clearSilentAuthAttempt,
  isSilentAuthError,
  login,
} from '@/config/auth';
import type { KeycloakRealm } from '@/integrations/keycloak/client';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { getLandingRouteFromToken } from "@/utils/landingRoute";

/**
 * Página de callback OAuth2 — captura o código e troca por tokens.
 * Rota /login-redirect → Realm Generator; /login-redirect/musa → Realm Musa.
 */
const LoginRedirect = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processOAuthCallback = async () => {
      const alreadyAuth = isAuthenticated();
      if (alreadyAuth) {
        sessionStorage.removeItem('oauth_callback_processed');
        clearSilentAuthAttempt();
        window.location.href =
          consumePostLoginRedirect() ?? getLandingRouteFromToken();
        return;
      }

      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Resposta do `prompt=none` quando não há sessão SSO viva. Não é falha:
      // é o Keycloak dizendo "precisa de interação". Mostrar tela de erro aqui
      // seria expor detalhe de protocolo, então cai direto no formulário do
      // generator — o destino guardado em sessionStorage sobrevive, porque só é
      // consumido quando o login dá certo.
      if (isSilentAuthError(errorParam)) {
        clearLastRealm();
        login();
        return;
      }

      if (errorParam) {
        setError(errorDescription || errorParam);
        return;
      }

      if (!code) {
        setError('Código de autorização não encontrado. Por favor, tente fazer login novamente.');
        return;
      }

      const realm: KeycloakRealm = location.pathname.includes('/login-redirect/musa') ? 'musa' : 'generator';

      try {
        await exchangeCodeForToken(code, realm);
        
        // Verificar se tokens foram salvos
        const nowAuth = isAuthenticated();
        
        if (!nowAuth) {
          setError('Erro ao salvar tokens. Por favor, tente novamente.');
          return;
        }

        // Login concluído: libera a trava anti-loop, senão uma expiração mais
        // tarde nesta mesma aba iria direto pro formulário, sem tentar o SSO.
        clearSilentAuthAttempt();

        // Volta para a rota que originou o login (ex.: link do menu aberto em
        // nova aba); sem destino guardado, cai na dashboard.
        window.location.href =
          consumePostLoginRedirect() ?? getLandingRouteFromToken();

      } catch (err: any) {
        setError(err.message || 'Erro ao completar autenticação. Por favor, tente novamente.');
      }
    };
    
    processOAuthCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Erro na autenticação</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button 
            className="w-full" 
            onClick={() => {
              sessionStorage.clear();
              const backPath = location.pathname.includes('/login-redirect/musa') ? '/login/musa' : '/login';
              navigate(backPath, { replace: true });
            }}
          >
            Voltar para o login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Autenticando...</p>
      </div>
    </div>
  );
};

export default LoginRedirect;
